import React, { useEffect, useState } from 'react';
import PageHeaderComponent, { PageHeaderProps } from '@components/layout/PageHeader';
import { PrimaryThemeProvider, MoodThemeProvider } from '@components/blocks/BlockThemeProvider';
import Favicon from '@components/app/Favicon';
import { MySchedule, Notifications } from '@components/header/flyouts';
import InputDeviceActivity from '@components/app/InputDeviceActivity';
import { useTranslate } from 'nucleus-text';
import useMeQuery from '@hooks/useMeQuery';
import useLogout from '@hooks/useLogout';
import useAppFeatures from '@hooks/useAppFeatures';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import { useAttendeeMessaging } from '@context/AttendeeMessagingContext';
import { User } from '@graphql/gql/graphql';
import { EngagementToastsConnected } from '@components/engagement/EngagementToasts';
import { createPortal } from 'react-dom';
import useEngagementMessages from '@components/engagement/useEngagementMessages';
import { TOAST_PORTAL_CONTAINER_ID } from '@components/engagement/constants';
import usePendingConnectionCount from '@components/connections/hooks/usePendingConnectionCount';
import useUnreadReportPostCount from '@components/communitySafety/hooks/useUnreadReportPostCount';
import MessagingFlyout from '@components/attendeeMessaging/flyout/MessagingFlyout';
import { useAppointmentPermissions } from '@components/appointments/utilities/useAppointmentPermissions';
import useEventNavigationSettings from './useEventNavigationSettings';

export const useTabDefinitions = () => {
  const { tabs } = useEventNavigationSettings();

  return tabs;
};

interface Tab {
  label: string;
  rawLabel?: string;
  value: string;
  testID: string;
  page: string;
  fullUrl: string;
}
interface Props extends PageHeaderProps {
  tabs: Record<string, Tab>;
  /** @deprecated Use `<Head><title>Title Goes here</title></Head>` instead */
  contentTitle?: string;
  hasMySchedule?: boolean;
  navHeaderVisible?: boolean;
  isAttendeeProfileEnabled: boolean;
}

function Header(props: Props): JSX.Element {
  const { translate } = useTranslate();
  const { attendeeYourEventsPage } = useAppFeatures();
  const { isFeatureSettingEnabled } = useAttendeeHubConfig();

  const {
    selectedTab,
    contentTitle,
    hideNavBar,
    pages,
    tabs,
    hasLinks,
    hasMySchedule,
    isAttendeeProfileEnabled,
    navHeight,
    navHeaderVisible,
    showLogoLink = true
  } = props;
  const tabOptions = Object.values(tabs);
  const attendeeMessagingEnabled = isFeatureSettingEnabled('MESSAGING');
  const yourEventsEnabled = isFeatureSettingEnabled('YOUR_EVENTS');
  const { unreadCount } = useAttendeeMessaging();

  const logout = useLogout();
  const { data, loading } = useMeQuery();
  const { event, ...me } = data?.me || ({} as User);
  const eventId = event?.id;
  const attendeeId = me?.id;
  const eventTitle = event?.title;
  const surveyUrl = event?.links?.surveys;
  const themeInfo = event?.eventTheme || {};
  const logo = themeInfo.logoImageOptimized || themeInfo.logoImage;

  const { unreadReportCount } = useUnreadReportPostCount(eventId, attendeeId);

  let title;
  if (contentTitle) {
    // use the session name or exhibitor name as title first
    title = contentTitle;
  } else if (selectedTab) {
    // when not viewing a specific entity, use tab title
    const selected = tabOptions.find(tab => tab.value === selectedTab);
    title = selected?.label || '';
  }
  if (eventTitle) {
    // always try to append the event title
    title = title ? `${title} - ${eventTitle}` : eventTitle;
  }

  const isConnectionsEnabled = isFeatureSettingEnabled('ATTENDEE_CONNECTIONS') && data?.me?.guest === false;

  const { pendingConnectionCount } = usePendingConnectionCount({
    attendeeId,
    eventId,
    skip: !isConnectionsEnabled,
    realtimeUpdates: true
  });

  const { canViewAppts } = useAppointmentPermissions();

  const isPushNotificationsEnabled = isFeatureSettingEnabled('PLANNER_PUSH_NOTIFICATIONS');

  /* ---------------------------------- Flyouts Components ------------------------------- */
  const flyoutControls = [
    hasMySchedule ? (
      <MySchedule
        eventId={eventId}
        appointmentsEnabled={canViewAppts}
        hasSessionList={event?.features?.sessions}
        navHeight={navHeight}
        admissionItemId={data?.me?.admissionItem?.id}
      />
    ) : null,
    isPushNotificationsEnabled ? (
      <Notifications
        eventId={eventId}
        surveyUrl={surveyUrl}
        navHeight={navHeight}
        navHeaderVisible={navHeaderVisible}
      />
    ) : null,
    attendeeMessagingEnabled ? <MessagingFlyout /> : null
  ].filter(flyout => flyout);
  /* ----------------------------------------------------------------------------------- */

  return loading ? null : (
    <>
      <InputDeviceActivity />
      <Favicon />
      <PageHeaderComponent
        {...props}
        eventId={eventId}
        tabOptions={tabOptions}
        title={title}
        attendeeName={translate('Universal-FullName', {
          firstName: me?.firstName,
          lastName: me?.lastName
        })}
        avatarName={translate('Universal-AvatarName', {
          firstName: me?.firstName,
          lastName: me?.lastName
        })}
        pages={pages}
        hideNavBar={hideNavBar}
        attendeeEmail={me?.email}
        attendeeProfilePhotoSrc={me?.profilePicture}
        isAttendeeProfileEnabled={isAttendeeProfileEnabled}
        eventTitle={event?.title}
        onLogout={() => logout()}
        logoSrc={logo}
        flyoutControls={flyoutControls}
        viewYourEvents={attendeeYourEventsPage && yourEventsEnabled}
        viewConnections={isConnectionsEnabled}
        pendingConnectionsCount={pendingConnectionCount}
        unreadReportCount={unreadReportCount}
        unreadMessageCount={unreadCount}
        hasLinks={hasLinks}
        showLogoLink={showLogoLink}
      />
    </>
  );
}

/**
 * Component to check the type of navigation configured for the event. It uses the feature settings
 * to either render a Header with links (regular events) or a simple header for events configured
 * without or simple navigation (i.e. self service events)
 *
 * @param {*} props
 * @returns
 */
function PageHeader(props) {
  const { hasLinks, hasMySchedule, pages, tabs, isAttendeeProfileEnabled, isWebinarNav } = useEventNavigationSettings();

  const { toastContainerId } = useEngagementMessages();

  const [toastElement, setToastElement] = useState<HTMLElement>(null);
  useEffect(() => {
    setToastElement(document.getElementById(toastContainerId || TOAST_PORTAL_CONTAINER_ID));
  }, [toastContainerId]);

  return (
    <>
      <PrimaryThemeProvider>
        <Header
          {...props}
          pages={pages}
          tabs={tabs}
          hasLinks={hasLinks}
          showLogoLink={!isWebinarNav}
          hasMySchedule={hasMySchedule}
          isAttendeeProfileEnabled={isAttendeeProfileEnabled}
        />
      </PrimaryThemeProvider>
      {toastElement &&
        createPortal(
          <MoodThemeProvider>
            <EngagementToastsConnected />
          </MoodThemeProvider>,
          toastElement
        )}
    </>
  );
}

export default PageHeader;
